import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { useLabelledBy, useControls } from '../../../hooks/useAccessibleName';
import { useAccordionKeyboardNavigation } from './useAccordionKeyboardNavigation';
import { useAccordionController } from './AccordionEventHandler';
import { AccordionEvent } from './index';

type AccordionItemContextType = {
  // Disclosure state and controls
  isOpen: boolean;
  toggle: () => void;
  setIsOpen: (isOpen: boolean) => void;

  // Accessibility IDs
  buttonId: string;
  contentId: string;

  // Accordion controller state
  isAlwaysOpen: boolean;
  allowMultiple: boolean;
  disabled: boolean;

  // Keyboard navigation
  handleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;

  // Button ref for keyboard navigation
  buttonRef: React.RefObject<HTMLButtonElement>;
};

const AccordionItemContext = createContext<
  AccordionItemContextType | undefined
>(undefined);

type AccordionItemProviderProps = {
  children: React.ReactNode;
  initiallyOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

export const AccordionItemProvider: React.FC<AccordionItemProviderProps> = ({
  children,
  initiallyOpen = false,
  onToggle,
}) => {
  const accordionController = useAccordionController();
  // Use accessibility hooks for proper IDs
  const [buttonId] = useLabelledBy();
  const [contentId] = useControls();

  // Use the useDisclosure hook for state management - start with initiallyOpen value
  const disclosure = useDisclosure(initiallyOpen, contentId);

  // Create a ref for this accordion item to register with event handler
  const itemRef = useRef<object>({});

  // Enhanced toggle function that integrates with accordion controller
  const enhancedToggle = useCallback(() => {
    const nextState = !disclosure.isOpen;
    const event = nextState ? AccordionEvent.OPEN : AccordionEvent.CLOSE;

    if (accordionController.dispatch(event)) {
      disclosure.toggle();
    }
  }, [disclosure.isOpen, disclosure.toggle, accordionController.dispatch]);

  // Enhanced setIsOpen function that integrates with accordion controller
  const enhancedSetIsOpen = useCallback(
    (isOpen: boolean) => {
      const event = isOpen ? AccordionEvent.OPEN : AccordionEvent.CLOSE;

      if (accordionController.dispatch(event)) {
        disclosure.setIsOpen(isOpen);
      }
    },
    [accordionController.dispatch, disclosure.setIsOpen]
  );

  // Report every open/close after mount, whether the user clicked or the controller forced it
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onToggle?.(disclosure.isOpen);
  }, [disclosure.isOpen]);

  // Determine if this accordion item should be disabled
  const disabled = false; // Simplified for now

  // Register with the accordion event handler
  useEffect(() => {
    const reducer = (event: symbol, value?: boolean) => {
      if (event === AccordionEvent.SET_OPEN) {
        // Directly set the state without going through dispatch
        disclosure.setIsOpen(!!value);
        return true;
      }

      if (event === AccordionEvent.IS_OPEN) {
        // Return the current state
        return disclosure.isOpen;
      }
      return true;
    };

    accordionController.subscribe(itemRef.current, reducer);

    return () => {
      accordionController.unsubscribe(itemRef.current);
    };
  }, [accordionController, disclosure.setIsOpen]);

  // Trigger checkOpen after subscription to handle isAlwaysOpen and allowMultiple logic
  useEffect(() => {
    // Use a timeout to ensure all items have registered before checking
    const timeoutId = setTimeout(() => {
      accordionController.checkOpen();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [accordionController]);

  // Keyboard navigation hook
  const { buttonRef, handleKeyDown } = useAccordionKeyboardNavigation({
    onToggle: enhancedToggle,
    disabled,
  });

  const contextValue = useMemo(
    () => ({
      isOpen: disclosure.isOpen,
      toggle: enhancedToggle,
      setIsOpen: enhancedSetIsOpen,
      buttonId,
      contentId,
      isAlwaysOpen: accordionController.isAlwaysOpen,
      allowMultiple: accordionController.allowMultiple,
      disabled,
      handleKeyDown,
      buttonRef,
    }),
    [
      disclosure.isOpen,
      enhancedToggle,
      enhancedSetIsOpen,
      buttonId,
      contentId,
      accordionController.isAlwaysOpen,
      accordionController.allowMultiple,
      disabled,
      handleKeyDown,
      buttonRef,
    ]
  );

  return (
    <AccordionItemContext.Provider value={contextValue}>
      {children}
    </AccordionItemContext.Provider>
  );
};

export const useAccordionItem = (): AccordionItemContextType => {
  const context = useContext(AccordionItemContext);
  if (!context) {
    throw new Error(
      'useAccordionItem must be used within an AccordionItemProvider'
    );
  }
  return context;
};
